import React, { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import api, { formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { ArrowLeft, Briefcase, UserPlus } from "lucide-react";
import { useCustomerAuth } from "@/contexts/CustomerAuthContext";

export default function PublicCareers() {
  const { id } = useParams();
  if (id) return <CareerDetail id={id} />;
  return <CareerList />;
}

function CareerList() {
  const [items, setItems] = useState([]);
  useEffect(() => { api.get("/public/careers?tenant_slug=abrah").then(({ data }) => setItems(data)); }, []);
  return (
    <div className="max-w-4xl mx-auto px-4 sm:px-6 py-10">
      <h1 className="font-display text-3xl font-bold text-gray-900 mb-2">Careers at Abrah</h1>
      <p className="text-sm text-gray-600 mb-8">Open roles across all branches — apply online in under 2 minutes.</p>
      {items.length === 0 ? (
        <div className="bg-white border border-dashed border-gray-300 rounded-md p-10 text-center text-sm text-gray-600">
          No open positions right now. Check back soon.
        </div>
      ) : (
        <ul className="space-y-3" data-testid="careers-list">
          {items.map((c) => (
            <li key={c.id}>
              <Link to={`/site/careers/${c.id}`}
                className="block bg-white border border-gray-200 rounded-md p-5 hover:border-emerald-900">
                <div className="flex items-start gap-3">
                  <div className="h-10 w-10 rounded-md bg-emerald-50 text-emerald-900 border border-emerald-200 flex items-center justify-center">
                    <Briefcase size={18} />
                  </div>
                  <div className="flex-1">
                    <div className="font-display font-semibold text-gray-900">{c.title}</div>
                    <div className="text-xs text-gray-500 mt-0.5">
                      {c.department || "—"} · {c.location || "Uganda"} · {(c.employment_type || "").replaceAll("_", " ")}
                    </div>
                    <p className="text-sm text-gray-600 mt-2 line-clamp-2">{c.description}</p>
                  </div>
                </div>
              </Link>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

function CareerDetail({ id }) {
  const { customer } = useCustomerAuth();
  const [career, setCareer] = useState(null);
  const [form, setForm] = useState({ full_name: "", email: "", phone: "", cover_letter: "" });
  const [cv, setCv] = useState(null);
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(false);
  useEffect(() => {
    api.get("/public/careers?tenant_slug=abrah")
      .then(({ data }) => setCareer(data.find((c) => c.id === id) || null));
  }, [id]);
  // Pre-fill from customer profile once loaded
  useEffect(() => {
    if (customer) {
      setForm((f) => ({
        ...f,
        full_name: f.full_name || customer.full_name || "",
        email: f.email || customer.email || "",
        phone: f.phone || customer.phone || "",
      }));
    }
  }, [customer]);
  const apply = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      let cv_file_id = null;
      if (cv) {
        // public upload — uses authenticated route, may fail if no session.
        // We still attempt; if it fails, application proceeds without CV.
        try {
          const fd = new FormData(); fd.append("file", cv);
          const { data: up } = await api.post("/files/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
          cv_file_id = up.id;
        } catch (_) { /* skip cv silently for public uploads */ }
      }
      await api.post("/public/careers/apply", {
        tenant_slug: "abrah", career_id: id, ...form, cv_file_id,
      });
      setDone(true);
      toast.success("Application submitted");
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };
  if (!career) return <div className="max-w-4xl mx-auto p-10 text-sm text-gray-600">Loading…</div>;
  return (
    <div className="max-w-3xl mx-auto px-4 sm:px-6 py-10">
      <Link to="/site/careers" className="text-sm text-emerald-900 inline-flex items-center gap-1 mb-4">
        <ArrowLeft size={14} /> All openings
      </Link>
      <h1 className="font-display text-3xl font-bold text-gray-900">{career.title}</h1>
      <div className="text-sm text-gray-500 mt-1">
        {career.department || "—"} · {career.location || "Uganda"} · {(career.employment_type || "").replaceAll("_", " ")}
      </div>
      <div className="prose prose-sm mt-6 max-w-none">
        <h3 className="font-display font-semibold text-gray-900">About the role</h3>
        <p className="whitespace-pre-wrap text-gray-700">{career.description}</p>
        {career.requirements && (<>
          <h3 className="font-display font-semibold text-gray-900 mt-4">Requirements</h3>
          <p className="whitespace-pre-wrap text-gray-700">{career.requirements}</p>
        </>)}
      </div>

      <div className="mt-8 bg-white border border-gray-200 rounded-md p-5">
        <h2 className="font-display text-xl font-bold text-gray-900 mb-4">Apply for this role</h2>
        {!customer && !done && (
          <div className="mb-4 bg-amber-50 border border-amber-200 rounded-lg p-3 flex items-center gap-3 text-sm">
            <UserPlus size={16} className="text-amber-800 shrink-0" />
            <div className="flex-1 text-amber-900">
              <Link to="/site/register" state={{ next: `/site/careers/${id}` }} className="underline font-semibold">Create an account</Link> or{" "}
              <Link to="/site/login" state={{ next: `/site/careers/${id}` }} className="underline font-semibold">sign in</Link>{" "}
              to track this application and future ones from your dashboard.
            </div>
          </div>
        )}
        {done ? (
          <div className="bg-emerald-50 border border-emerald-200 rounded-md p-4 text-emerald-900">
            ✓ Application received. We&apos;ll reach out within 7 business days.
            {customer && (
              <div className="mt-2 text-sm">
                Track it any time at{" "}
                <Link to="/site/account" className="underline font-semibold">My account → Applications</Link>.
              </div>
            )}
          </div>
        ) : (
          <form onSubmit={apply} className="grid grid-cols-1 md:grid-cols-2 gap-3" data-testid="apply-form">
            <div>
              <Label>Full name</Label>
              <Input required value={form.full_name}
                onChange={(e) => setForm({ ...form, full_name: e.target.value })}
                className="mt-1" data-testid="apply-name-input" />
            </div>
            <div>
              <Label>Email</Label>
              <Input type="email" required value={form.email}
                onChange={(e) => setForm({ ...form, email: e.target.value })}
                className="mt-1" data-testid="apply-email-input" />
            </div>
            <div className="md:col-span-2">
              <Label>Phone</Label>
              <Input required value={form.phone}
                onChange={(e) => setForm({ ...form, phone: e.target.value })}
                className="mt-1" data-testid="apply-phone-input" />
            </div>
            <div className="md:col-span-2">
              <Label>Cover letter</Label>
              <Textarea rows={4} value={form.cover_letter}
                onChange={(e) => setForm({ ...form, cover_letter: e.target.value })} className="mt-1" />
            </div>
            <div className="md:col-span-2">
              <Label>CV (optional)</Label>
              <Input type="file" accept=".pdf,.doc,.docx" onChange={(e) => setCv(e.target.files?.[0])} className="mt-1" />
              <div className="text-xs text-gray-500 mt-1">PDF or Word document.</div>
            </div>
            <Button type="submit" disabled={busy} className="md:col-span-2 bg-emerald-900 hover:bg-emerald-950 text-white"
              data-testid="apply-submit-button">
              {busy ? "Submitting…" : "Submit application"}
            </Button>
          </form>
        )}
      </div>
    </div>
  );
}
